import { BaseCanvasPack } from "./BaseCanvasPack";

export default class MadScienceCanvasPack extends BaseCanvasPack {
  drawBackground(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number,
    level?: number
  ): void {
    // Dark green/steel grey tiled walls
    // Use a gradient to give depth to the wall
    const wallGradient = context.createLinearGradient(0, 0, width, height);
    wallGradient.addColorStop(0, "#1a2f2f"); // Darker top-left
    wallGradient.addColorStop(1, "#2f4f4f"); // Lighter bottom-right
    context.fillStyle = wallGradient;
    context.fillRect(0, 0, width, height);

    // Tile pattern with some depth
    context.strokeStyle = "rgba(0, 0, 0, 0.1)"; // Much more subtle grid lines
    context.lineWidth = 1;
    const tileSize = 40;

    for (let x = 0; x < width; x += tileSize) {
      for (let y = 0; y < height; y += tileSize) {
        // Draw individual tile with slight gradient
        const tileGradient = context.createLinearGradient(
          x,
          y,
          x + tileSize,
          y + tileSize
        );
        tileGradient.addColorStop(0, "rgba(255, 255, 255, 0.01)"); // Reduced opacity
        tileGradient.addColorStop(1, "rgba(0, 0, 0, 0.01)"); // Reduced opacity
        context.fillStyle = tileGradient;
        context.fillRect(x, y, tileSize, tileSize);
        context.strokeRect(x, y, tileSize, tileSize);
      }
    }

    if (frameTime !== undefined) {
      this.drawBubbles(context, width, height, frameTime);
    }
  }

  drawFrame(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {
    const borderWidth = 12;

    // Insulator/High Voltage border
    // Ceramic look (ribbed pattern)
    const ribSize = 10;

    // Helper to draw ribbed ceramic segment
    const drawRibbedSegment = (
      x1: number,
      y1: number,
      x2: number,
      y2: number,
      vertical: boolean
    ) => {
      const length = vertical ? Math.abs(y2 - y1) : Math.abs(x2 - x1);
      const count = Math.floor(length / ribSize);

      for (let i = 0; i < count; i++) {
        const pos = i * ribSize;
        context.fillStyle = i % 2 === 0 ? "#e0e0e0" : "#d0d0d0"; // Alternating shades for ribs

        if (vertical) {
          // Draw rounded rect for rib
          const cx = x1;
          const cy = Math.min(y1, y2) + pos + ribSize / 2;
          context.beginPath();
          context.ellipse(
            cx,
            cy,
            borderWidth / 2 + 2,
            ribSize / 2,
            0,
            0,
            Math.PI * 2
          );
          context.fill();
        } else {
          const cx = Math.min(x1, x2) + pos + ribSize / 2;
          const cy = y1;
          context.beginPath();
          context.ellipse(
            cx,
            cy,
            ribSize / 2,
            borderWidth / 2 + 2,
            0,
            0,
            Math.PI * 2
          );
          context.fill();
        }
      }
    };

    // Draw ribs along sides
    drawRibbedSegment(
      borderWidth / 2,
      borderWidth,
      borderWidth / 2,
      height - borderWidth,
      true
    ); // Left
    drawRibbedSegment(
      width - borderWidth / 2,
      borderWidth,
      width - borderWidth / 2,
      height - borderWidth,
      true
    ); // Right
    drawRibbedSegment(
      borderWidth,
      borderWidth / 2,
      width - borderWidth,
      borderWidth / 2,
      false
    ); // Top
    drawRibbedSegment(
      borderWidth,
      height - borderWidth / 2,
      width - borderWidth,
      height - borderWidth / 2,
      false
    ); // Bottom

    // Metallic contacts (corners)
    context.fillStyle = "#708090"; // Slate gray (metal)
    const contactSize = 16;

    const drawContact = (x: number, y: number) => {
      // Metal base
      context.fillStyle = "#708090";
      context.beginPath();
      context.arc(x, y, contactSize / 2, 0, Math.PI * 2);
      context.fill();

      // Highlight
      context.fillStyle = "#a0b0c0";
      context.beginPath();
      context.arc(x - 2, y - 2, contactSize / 4, 0, Math.PI * 2);
      context.fill();

      // Bolt
      context.fillStyle = "#405060";
      context.beginPath();
      context.arc(x, y, 3, 0, Math.PI * 2);
      context.fill();
    };

    // Corners
    drawContact(borderWidth / 2, borderWidth / 2);
    drawContact(width - borderWidth / 2, borderWidth / 2);
    drawContact(borderWidth / 2, height - borderWidth / 2);
    drawContact(width - borderWidth / 2, height - borderWidth / 2);

    // Electric Arcs
    if (frameTime !== undefined) {
      this.drawElectricArcs(context, width, height, frameTime, borderWidth);
    }
  }

  drawOverlay(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {
    // None
  }

  private drawBubbles(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number
  ): void {
    context.save();

    // Draw bubbles rising from bottom
    const bubbleCount = 15;
    for (let i = 0; i < bubbleCount; i++) {
      const seed = i * 137;
      const speed = 0.05 + (seed % 0.05);

      // Position based on time
      const yOffset = (frameTime * speed + seed * 100) % (height + 50);
      const y = height - yOffset + 25;

      // Wiggle x
      const xBase = (seed * 17) % width;
      const xWiggle = Math.sin(frameTime * 0.002 + seed) * 10;
      const x = xBase + xWiggle;

      const size = 3 + (seed % 5);

      // Only draw if visible
      if (y < height && y > 0) {
        context.beginPath();
        context.arc(x, y, size, 0, Math.PI * 2);

        // Bubble style
        context.fillStyle = "rgba(100, 255, 100, 0.3)"; // Greenish body
        context.fill();

        context.strokeStyle = "rgba(200, 255, 200, 0.5)"; // Light outline
        context.lineWidth = 1;
        context.stroke();

        // Highlight
        context.beginPath();
        context.arc(x - size * 0.3, y - size * 0.3, size * 0.2, 0, Math.PI * 2);
        context.fillStyle = "rgba(255, 255, 255, 0.6)";
        context.fill();
      }
    }
    context.restore();
  }

  private drawElectricArcs(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number,
    borderWidth: number
  ): void {
    // Frequent, random arcs
    // Use a noise function or random based on time to determine if arc fires
    // Fire more frequently
    if (Math.random() > 0.3) return;

    context.save();
    context.lineCap = "round";
    context.lineJoin = "round";

    // Pick two random points on the border
    const getBorderPoint = () => {
      const side = Math.floor(Math.random() * 4);
      const pos = Math.random();
      if (side === 0) return { x: width * pos, y: borderWidth / 2 }; // Top
      if (side === 1) return { x: width - borderWidth / 2, y: height * pos }; // Right
      if (side === 2) return { x: width * pos, y: height - borderWidth / 2 }; // Bottom
      return { x: borderWidth / 2, y: height * pos }; // Left
    };

    const p1 = getBorderPoint();
    // Pick p2 relatively close to p1 for "crawling" arcs, or far for big jumps
    // Let's do mostly crawling arcs along the border
    let p2;
    if (Math.random() > 0.2) {
      // Crawl along border
      p2 = {
        x: p1.x + (Math.random() - 0.5) * 200,
        y: p1.y + (Math.random() - 0.5) * 200,
      };
      // Clamp to border area roughly
      if (p2.x < 0) p2.x = borderWidth / 2;
      if (p2.x > width) p2.x = width - borderWidth / 2;
      if (p2.y < 0) p2.y = borderWidth / 2;
      if (p2.y > height) p2.y = height - borderWidth / 2;
    } else {
      // Jump across
      p2 = getBorderPoint();
    }

    // Draw jagged line
    context.beginPath();
    context.moveTo(p1.x, p1.y);

    const dist = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
    const segments = Math.floor(dist / 10);

    let currX = p1.x;
    let currY = p1.y;

    for (let i = 0; i < segments; i++) {
      const t = (i + 1) / segments;
      const targetX = p1.x + (p2.x - p1.x) * t;
      const targetY = p1.y + (p2.y - p1.y) * t;

      // Jitter
      // const jitter = (Math.random() - 0.5) * 15;
      currX = targetX + (Math.random() - 0.5) * 10; // Perpendicular jitter would be better but random is okay for chaos
      currY = targetY + (Math.random() - 0.5) * 10;

      context.lineTo(currX, currY);
    }
    context.lineTo(p2.x, p2.y);

    // Glow effect
    context.shadowBlur = 15;
    context.shadowColor = "#00ffff";
    context.strokeStyle = "#e0ffff";
    context.lineWidth = 2;
    context.stroke();

    // Inner core
    context.shadowBlur = 0;
    context.strokeStyle = "#ffffff";
    context.lineWidth = 1;
    context.stroke();

    context.restore();
  }

  getBorderStyle(): string {
    return "12px solid #dcdcdc";
  }

  getBoxShadow(frameTime?: number): string {
    return "0 0 20px rgba(0, 255, 255, 0.4)";
  }

  getBackdropFilter(): string {
    return "blur(2px)";
  }
}
